/**
 * SendTestReportModal — "Rapport genereren" action for a tenant.
 *
 * POSTs to /tenants/{slug}/deliveries/send-test (SendTestReportIn). This is a
 * real, side-effecting action: the backend renders a test report and SENDS it
 * via SMTP to the tenant's configured recipients. The modal therefore warns
 * explicitly and requires a deliberate submit. Mirrors TenantCreateModal /
 * DomainAddModal in shape and behaviour.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';

import { ApiError } from '@/lib/api';
import { apiPost } from '@/features/_shared/queries';
import { toast } from '@/lib/toast';

interface Props {
  open: boolean;
  onClose: () => void;
  slug: string;
}

type Period = 'weekly' | 'monthly';

interface SendTestReportResult {
  period: Period;
  period_start: string;
  tenant_id: string;
  enqueued: boolean;
}

export function SendTestReportModal({ open, onClose, slug }: Props): JSX.Element | null {
  const qc = useQueryClient();
  const [period, setPeriod] = useState<Period>('weekly');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPeriod('weekly');
      setError(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      apiPost<SendTestReportResult>(
        `/tenants/${slug}/deliveries/send-test`,
        { period },
        { tenant: slug },
      ),
    onSuccess: () => {
      toast({ tone: 'success', message: 'Testrapport wordt gegenereerd en verzonden.' });
      void qc.invalidateQueries({ queryKey: ['deliveries', slug] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.problem.detail ?? err.problem.title ?? 'Verzenden mislukt.');
      } else {
        setError('Onbekende fout bij verzenden.');
      }
    },
  });

  if (!open) return null;

  const canSubmit = !mutation.isPending;

  const handleSubmit = (evt: FormEvent<HTMLFormElement>): void => {
    evt.preventDefault();
    setError(null);
    if (!canSubmit) return;
    mutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
      role="dialog"
      aria-modal="true"
      aria-label="Testrapport genereren"
      onClick={onClose}
    >
      <div
        className="w-[440px] max-w-[95vw] rounded-md bg-white shadow-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-[14px] font-semibold text-slate-900">Testrapport genereren</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="text-slate-400 hover:text-slate-900"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 p-4 text-[13px]">
          <div>
            <label className="mb-1 block font-medium text-slate-700" htmlFor="report-period">
              Periode
            </label>
            <select
              id="report-period"
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
              className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
            >
              <option value="weekly">Wekelijks (vorige week)</option>
              <option value="monthly">Maandelijks (vorige maand)</option>
            </select>
            <p className="mt-1 text-[11px] text-slate-500">
              Het rapport wordt gerenderd voor de vorige volledige periode.
            </p>
          </div>

          <div
            role="alert"
            className="rounded border border-amber-200 bg-amber-50 px-2.5 py-2 text-[12px] text-amber-800"
          >
            Let op: hiermee wordt een <strong>echt</strong> testrapport per e-mail verzonden naar
            alle ingestelde contacten van deze tenant. Gebruik dit alleen bewust.
          </div>

          {error && (
            <div
              role="alert"
              className="rounded border border-fail-100 bg-fail-50 px-2.5 py-1.5 text-[12px] text-fail-700"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 hover:bg-slate-50"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded bg-brand-600 px-3 py-1.5 font-medium text-slate-900 hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mutation.isPending ? 'Bezig…' : 'Verzenden'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
